/* eslint-disable prettier/prettier */
import React, { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';

import { Formik, FormikValues } from 'formik';
import moment from 'moment';
import { ColorPalette } from 'theme/colors';
import * as yup from 'yup';

import { ReactComponent as GroupIcon } from 'assets/images/group.svg';
import { ReactComponent as UserIcon } from 'assets/images/user.svg';
import ListPreview from 'components/AggregationCards/ListPreview';
import SimpleCard from 'components/AggregationCards/SimpleAggregation';
import Header from 'components/AppBar';
import ApplicationOverlayContent from 'components/ApplicationLayout/ApplicationOverlayContent';
import SubmitButton from 'components/Buttons/SubmitButton';
import BottomSheet from 'components/ConfirmBottomSheet';
import Field from 'components/Field';
import FormikSubmitErrorEffect from 'components/FormikSubmitErrorEffect';
import Input from 'components/Input';
import Loader from 'components/Loader';
import MonthSelect from 'components/MonthSelect';
import { Desktop, Mobile } from 'components/Responsive';
import ScreenHeader from 'components/ScreenHeader';
import Section from 'components/Section';
import GenreSelector from 'components/Selects/GenreSelector';
import NationalitySelector from 'components/Selects/OnboardingSelect';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useToast from 'hooks/useToast';
import { AdminUser, AllowedUser } from 'models/adminUser';
import { NationalityCountry } from 'models/country';
import { Group } from 'models/group';
import { GroupedListModel } from 'models/groupedList';
import GroupSelectList from 'screens/components/GroupSelectList';
import ResponseScreen, { BackgroundTypes } from 'screens/components/ResponseScreen';
import { htmlT } from 'utils/generic';
import { extractErrorMessage, formatDateToBack } from 'utils/generic';
import { getLanguageRegion } from 'utils/i18n';
import { isValidPhoneNumber } from 'utils/phoneValidation';

import GroupsAndUsersSelection from '../GroupsAndUsersSelection';
import AllowedUserSelect from './components/AllowedUserSelect';
import {
  Wrapper,
  FormContainer,
  Divider,
  GroupSelectLabel,
  ToggleCard,
  Checkbox,
  CheckboxLabel,
  TextTypeUser,
  UserGroupListContainer,
  Content,
  ErrorValidation,
  SectionDateOfBirth,
} from './styles';

interface UserFormProps {
  groupList: Group[];
  userId?: string;
  userList: AdminUser[];
  userData?: AdminUser;
  isUserLoading: boolean;
  country: string;
  companyId: string;
  id: string;
  saveUser: (user: AdminUser, companyId: string, id: string) => Promise<void>;
  fetchGroupList: () => Promise<void>;
  fetchUserList: () => Promise<void>;
  fetchCountryList: () => Promise<void>;
  loadingNationalityCountries: boolean;
  nationalityCountries: NationalityCountry[];
}

const USER_ICON = <UserIcon color={ColorPalette.HEX.DimGray} height={18} />;
const GROUP_ICON = <GroupIcon color={ColorPalette.HEX.DimGray} height={18} />;

const mapGroups = (groups: Group[]): GroupedListModel[] =>
  groups.map((group) => ({
    id: group.id,
    name: group.name,
    badge: !!group.rule ? { id: group.rule.id, name: group.rule.name } : { id: '', name: '' },
    entity: 'group',
    entityTypeImage: GROUP_ICON,
  }));

const mapUsers = (users: AdminUser[]): GroupedListModel[] =>
  users.map((user) => ({
    id: user.id || '',
    name: user.name,
    lastName: user.lastName,
    email: user.email,
    userGroupId: user.userGroupId || '',
    userGroupName: user.userGroupName || '',
    entity: 'user',
    entityTypeImage: USER_ICON,
  }));

const UsersForm = ({
  userData,
  userId,
  isUserLoading,
  groupList,
  userList,
  country,
  saveUser,
  companyId,
  id,
  fetchCountryList,
  fetchGroupList,
  fetchUserList,
  loadingNationalityCountries,
  nationalityCountries,
}: UserFormProps) => {
  const history = useHistory();
  const { t } = useStoreTranslation();
  const [data, setData] = useState<AdminUser>();
  const [isGroupFetching, setGroupFetching] = useState(false);
  const [isUserFetching, setUserFetching] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [isGroupSelectionVisible, setGroupSelectionVisible] = useState(false);
  const [isGroupAndUsersSelectionVisible, setGroupsAndUsersSelectionVisible] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [selectedGroups, setSelectedGroups] = useState<GroupedListModel[]>([]);
  const selectedGroup = selectedGroups.length > 0 ? selectedGroups[0] : null;
  const [existingRecord, setExistingRecord] = useState(false);
  const toast = useToast();
  const stringGroup = userData?.id ? 'userForm.update' : 'userForm.create';
  const groupListSelect = useMemo(() => mapGroups(groupList), [groupList]);
  const userListSelect = useMemo(() => mapUsers(userList), [userList]);
  const [month, setMonth] = useState<number>();

  const handleSelectGroup = (group: GroupedListModel[]) => {
    setGroupSelectionVisible(false);
    setSelectedGroups(group);
  };

  useEffect(() => {
    const loadGroups = async () => {
      await fetchGroupList();
      setGroupFetching(false);
    };
    loadGroups();
  }, [fetchGroupList]);

  useEffect(() => {
    const loadUsers = async () => {
      await fetchUserList();
      setUserFetching(false);
    };
    loadUsers();
  }, [fetchUserList]);

  useEffect(() => {
    if (userData?.id && !nationalityCountries.length && !loadingNationalityCountries) {
      fetchCountryList();
    }
  }, [fetchCountryList, loadingNationalityCountries, nationalityCountries, userData]);

  useEffect(() => {
    if (userData?.id) {
      setExistingRecord(true);
      setData(userData);

      if (userData?.birthDate !== '0001-01-01T00:00:00Z') {
        setMonth(parseInt(moment.utc(userData?.birthDate).format('MM')));
      }
    }

    if (userData) {
      const userGroup = groupList.find((group) => group.id === userData.userGroupId);
      setSelectedGroups(userGroup ? mapGroups([userGroup]) : []);
    }
  }, [userData, groupList]);

  const handleConfirm = (values: AdminUser) => {

    let formattedMonth;
    let birthDate = '';

    if (isDateOfBirthValid()) {
      formattedMonth = month && month < 10 ? '0' + month : month;
      birthDate = `${values.birthDate_day}/${formattedMonth}/${values.birthDate_year}`;
    } else {
      setConfirm(false);
      return;
    }

    const newData: AdminUser = {
      ...data,
      ...values,
      isInvite: data?.isInvite === false ? false : true,
      userGroupId: selectedGroup?.id,
      country: getLanguageRegion(country),
      birthDate: month ? formatDateToBack(birthDate) : '',
      idRappi: userData?.idRappi,
      emailRappi: userData?.emailRappi,
      companyId,
    };
    setData(newData);
    setConfirm(true);
  };

  const save = async () => {
    try {
      setLoading(true);
      await saveUser(
        {
          ...data,
          email: data?.email?.toLowerCase(),
          usersBookAllowed: data?.usersBookAllowed?.filter((user) => user.id !== (userId || '')),
        } as AdminUser,
        companyId,
        id,
      );
      setConfirm(false);
      setShowSuccess(true);
    } catch (error) {
      setConfirm(false);
      window.scrollTo(0, 0);
      toast.add({
        type: 'danger',
        message: extractErrorMessage(error) || t('defaultError.default'),
      });
    } finally {
      setLoading(false);
    }
  };

  const validationSchema = yup.object().shape({
    name: yup.string().trim().max(100).required(),
    lastName: yup.string().trim().max(100).required(),
    phoneNumber: yup.string().phone(),
    email: yup.string().email().noPlusSign().required(),
    gender: yup.string(),
    nationality: yup.string(),
    birthDate_day: yup.number().positive().moreThan(0).lessThan(32, t('guestForm.day.invalidNumber')),
    birthDate_year: yup.number().moreThan(1910).lessThan(new Date().getFullYear() + 1, t('guestForm.year.invalidNumber')),
    documentNumber: yup.string().trim().max(100),
    passportNumber: yup.string().trim().max(100),
    type: yup.string().required(),
    sendWhatsapp: yup.boolean().required(),
    userGroupId: yup.string().required(),
    isTravelArranger: yup.bool().required(),
    isGuestBookAllowed: yup.bool().when('isTravelArranger', {
      is: true,
      then: yup.bool().required(),
    }),
    isAllUsersBookAllowed: yup.bool().when('isTravelArranger', {
      is: true,
      then: yup.bool().required(),
    }),
    groupsBookAllowed: yup
      .array()
      .test('travelArranger-groups', 'userForm.travelerUsersAllowed.validation', function (value) {
        const { isTravelArranger, isAllUsersBookAllowed, usersBookAllowed } = (this as yup.TestContext).parent;

        if (
          isTravelArranger &&
          isAllUsersBookAllowed === false &&
          usersBookAllowed?.length === 0 &&
          value?.length === 0
        )
          return false;
        return true;
      }),
    usersBookAllowed: yup
      .array()
      .test('travelArranger-users', 'userForm.travelerUsersAllowed.validation', function (value) {
        const { isTravelArranger, isAllUsersBookAllowed, groupsBookAllowed } = (this as yup.TestContext).parent;

        if (
          isTravelArranger &&
          isAllUsersBookAllowed === false &&
          groupsBookAllowed?.length === 0 &&
          value?.length === 0
        )
          return false;
        return true;
      }),
  });

  const currentUserAsAllowedTraveler: AllowedUser & {
    disabled: boolean;
    userGroupId?: string;
    userGroupName?: string;
  } = {
    id: userData?.id || '',
    name: userData?.name || t('userForm.userDefaultValue'),
    lastName: userData?.lastName || '',
    email: userData?.email || '',
    disabled: true,
    userGroupId: userData?.userGroupId,
    userGroupName: userData?.userGroupName,
  };

  const usersBookAllowedWithCurrentUser = [
    currentUserAsAllowedTraveler,
    ...(userData?.usersBookAllowed || []).filter((user) => user.id !== currentUserAsAllowedTraveler.id),
  ];

  const initValues: AdminUser = {
    id: userData?.id || '',
    name: userData?.name || '',
    lastName: userData?.lastName || '',
    phoneNumber: userData?.phoneNumber || '',
    email: userData?.email || '',
    birthDate: userData?.birthDate === '0001-01-01T00:00:00Z' ? '' : userData?.birthDate || '',
    birthDate_day: userData?.birthDate === '0001-01-01T00:00:00Z' || userData?.birthDate === undefined ? '' : moment.utc(userData?.birthDate)?.format('DD') || '',
    birthDate_month: userData?.birthDate === '0001-01-01T00:00:00Z' || userData?.birthDate === undefined ? '' : moment.utc(userData?.birthDate)?.format('MM') || '',
    birthDate_year: userData?.birthDate === '0001-01-01T00:00:00Z' || userData?.birthDate === undefined ? '' : moment.utc(userData?.birthDate)?.format('YYYY') || '',
    gender: userData?.gender || 'Male',
    nationality: userData?.nationality || '',
    documentNumber: userData?.documentNumber || '',
    passportNumber: userData?.passportNumber || '',
    type: userData?.type || 'traveler',
    userGroupId: userData?.userGroupId === '00000000-0000-0000-0000-000000000000' ? '' : userData?.userGroupId,
    isTravelArranger: userData?.isTravelArranger || false,
    isGuestBookAllowed: userData?.isGuestBookAllowed || false,
    isAllUsersBookAllowed: userData?.isAllUsersBookAllowed,
    groupsBookAllowed:
      userData?.groupsBookAllowed?.map((item) => ({ ...item, entity: 'group', entityTypeImage: GROUP_ICON })) || [],
    usersBookAllowed:
      usersBookAllowedWithCurrentUser.map((item) => ({ ...item, entity: 'user', entityTypeImage: USER_ICON })) || [],
    sendWhatsapp: userData?.sendWhatsapp || false
  };

  const isEmailDisabled = () => {
    if (!data) return false;
    if (!data.email) return false;
    if (data.isInvite) return false;

    return true;
  };

  const handleChangeMonth = (month: number) => {
    setMonth(month);
  }

  const isDateOfBirthValid = () => {

    const birthDate_day = (document.getElementById('input_birthDate_day') as HTMLInputElement)?.value || '';
    const birthDate_year = (document.getElementById('input_birthDate_year') as HTMLInputElement)?.value || '';

    if (birthDate_day === '' && birthDate_year === '') {
      return true;
    }

    return moment(`${birthDate_year}-${month}-${birthDate_day}`, 'YYYY-MM-DD').isValid();
  };

  return (
    <Wrapper className="target">
      {isUserLoading || loadingNationalityCountries ? (
        <Loader fullScreen />
      ) : (
        <>
          <Mobile>
            <Header goBack={() => history.push('/usersList')}>{t(`${stringGroup}.title`)}</Header>
          </Mobile>

          <Desktop>
            <ScreenHeader title={t('userMenu.usersProfilesTitle')} subtitle={t('userMenu.usersProfilesDetail')} />
          </Desktop>
          <Formik
            initialStatus={{ rightCheckIfIsValid: true }}
            initialValues={initValues}
            validationSchema={validationSchema}
            onSubmit={(value: AdminUser) => handleConfirm(value)}
            isInitialValid={(formik: FormikValues) => validationSchema.isValidSync(formik.initialValues)}
          >
            {({ values, setValues, dirty, setFieldValue, errors, submitCount, isValid }: FormikValues) => (
              <>
                <FormikSubmitErrorEffect />

                <FormContainer $spacingBottom={!!userData?.id && dirty}>
                  <Section
                    title={t('userForm.basicInformations.title')}
                    details={t('userForm.basicInformations.description')}
                  >
                    <Field name="name" label={t('userForm.userName')}>
                      <Input
                        placeholder={t('userForm.firstnamePlaceHolder')}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                          setFieldValue(
                            'usersBookAllowed',
                            values.usersBookAllowed.map((user) => {
                              if (user.id !== (userData?.id || '')) return user;
                              return { ...user, name: event.target.value || t('userForm.userDefaultValue') };
                            }),
                          )
                        }
                      />
                    </Field>
                    <Field name="lastName" label={t('userForm.userLastName')}>
                      <Input
                        placeholder={t('userForm.lastnamePlaceHolder')}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                          setFieldValue(
                            'usersBookAllowed',
                            values.usersBookAllowed.map((user) => {
                              if (user.id !== (userData?.id || '')) return user;
                              return {
                                ...user,
                                lastName: event.target.value,
                                name:
                                  !event.target.value && !user.name
                                    ? t('userForm.userDefaultValue')
                                    : user.name === t('userForm.userDefaultValue')
                                      ? ''
                                      : user.name,
                              };
                            }),
                          )
                        }
                      />
                    </Field>

                    <Field name="email" label={t('userForm.email')}>
                      <Input
                        id="email"
                        type="email"
                        placeholder={t('userForm.emailPlaceHolder')}
                        disabled={isEmailDisabled()}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                          setFieldValue(
                            'usersBookAllowed',
                            values.usersBookAllowed.map((user) => {
                              if (user.id !== (userData?.id || '')) return user;
                              return {
                                ...user,
                                email: event.target.value,
                              };
                            }),
                          )
                        }
                      />
                    </Field>
                    {existingRecord ? (
                      <>
                        <Field name="phoneNumber" label={t('userForm.userPhoneNumber')}>
                          <Input type="text" inputMode="tel" placeholder={t('userForm.telNumberPlaceHolder')} />
                        </Field>

                        <Field name="gender" label={t('userForm.gender')}>
                          <GenreSelector id="gender" />
                        </Field>
                      </>
                    ) : null}
                  </Section>

                  {existingRecord ? (
                    <SectionDateOfBirth>
                      <div></div>
                      <Content>
                        <Field name="birthDate_day" label={t('guestForm.day.label')}>
                          <Input
                            type="text"
                            id="input_birthDate_day"
                            mask="99"
                            inputMode="numeric"
                            placeholder={t('guestForm.day.placeholder')}
                          />
                        </Field>
                        <Field name="birthDate_month">
                          <MonthSelect handleChangeDate={handleChangeMonth} initialValue={month?.toString()} name="input_birthDate_month" />
                        </Field>
                        <Field name="birthDate_year" label={t('guestForm.year.label')}>
                          <Input
                            type="text"
                            id="input_birthDate_year"
                            mask="9999"
                            inputMode="numeric"
                            placeholder={t('guestForm.year.placeholder')}
                          />
                        </Field>
                        {!isDateOfBirthValid() && (
                          <>
                            <ErrorValidation>
                              {t('guestForm.dateOfBirthInvalid')}
                            </ErrorValidation>
                          </>
                        )}
                        <div></div>
                      </Content>
                    </SectionDateOfBirth>
                  ) : null}

                  <Divider />

                  <Section title={t('userForm.travelInformations.title')}>
                    {existingRecord ? (
                      <>
                        <Field name="documentNumber" label={t('userForm.documentNumber')}>
                          <Input id="documentNumber" placeholder={t('userForm.documentPlaceholder')} />
                        </Field>

                        <Field name="passportNumber" label={t('userForm.passportNumber')}>
                          <Input id="passportNumber" placeholder={t('userForm.passportPlaceholder')} />
                        </Field>

                        <Field name="nationality" label={t('userForm.country')}>
                          <NationalitySelector
                            type="nationality"
                            options={nationalityCountries}
                            title={t('userForm.countrySelect')}
                            enableSearch={true}
                          />
                        </Field>
                      </>
                    ) : null}

                    <div>
                      <GroupSelectLabel isError={submitCount > 0 && !!errors.userGroupId}>
                        {t('userForm.addGroupLabel')}
                      </GroupSelectLabel>
                      <SimpleCard
                        title={selectedGroup?.name}
                        placeholder={t('userForm.addGroup')}
                        icon="user"
                        active={!!selectedGroup}
                        onClick={() => setGroupSelectionVisible(true)}
                        onAdd={() => setGroupSelectionVisible(true)}
                        errorMessage={submitCount > 0 ? errors.userGroupId : ''}
                      />
                      <ApplicationOverlayContent
                        sidebar
                        show={isGroupSelectionVisible}
                        onClose={() => setGroupSelectionVisible(false)}
                      >
                        <GroupSelectList
                          noAdd
                          singleSelect
                          isAlphabetic
                          showGroupsSelection
                          groupList={groupListSelect}
                          currentGroup={selectedGroups}
                          isLoading={isGroupFetching}
                          tourHasAccess
                          tourLoading={false}
                          onConfirmSelection={(group: GroupedListModel[]) => {
                            handleSelectGroup(group);
                            setFieldValue('userGroupId', group[0]?.id);
                            setFieldValue(
                              'usersBookAllowed',
                              values.usersBookAllowed.map((user) => {
                                if (user.id !== (userData?.id || '')) return user;
                                return { ...user, userGroupId: group[0]?.id, userGroupName: group[0]?.name };
                              }),
                            );
                          }}
                          onCloseSelectionScreen={() => setGroupSelectionVisible(false)}
                          changeModalTitleKey="userForm.group.bottomSheetTitle"
                          changeModalMessageKey="userForm.group.userHasGroup"
                        />
                      </ApplicationOverlayContent>
                    </div>
                  </Section>

                  <Divider />

                  <Section title={t('userForm.permissions.title')} details={t('userForm.permissions.description')}>
                    <ToggleCard
                      title={t('userForm.adminTitle')}
                      message={t('userForm.adminDescription')}
                      value={values.type.toLowerCase() === 'admin'}
                      onToggle={(_, value) => setFieldValue('type', value ? 'admin' : 'traveler')}
                    />
                    <ToggleCard
                      title={t('userForm.travelerTitle')}
                      message={t('userForm.travelerDescription')}
                      value={values.isTravelArranger}
                      onToggle={(_, value) => setFieldValue('isTravelArranger', value)}
                      errorMessage={submitCount > 0 ? errors.usersBookAllowed || errors.groupsBookAllowed : ''}
                    >
                      {values.isTravelArranger && (
                        <>
                          <Field name="isAllUsersBookAllowed" label={t('userForm.travelerUsersAllowed.label')}>
                            <AllowedUserSelect
                              id="isAllUsersBookAllowed"
                              onValueChange={(value) => {
                                if (!value) setGroupsAndUsersSelectionVisible(true);
                              }}
                            />
                          </Field>
                          {values.isAllUsersBookAllowed !== undefined && (
                            <Checkbox
                              checked={values.isGuestBookAllowed}
                              onChange={(value) => {
                                setFieldValue('isGuestBookAllowed', value);
                              }}
                              size={24}
                            >
                              <CheckboxLabel
                                onClick={() => {
                                  setFieldValue('isGuestBookAllowed', !values.isGuestBookAllowed);
                                }}
                              >
                                {t('userForm.travelerUsersAllowed.allowInvites')}
                              </CheckboxLabel>
                            </Checkbox>
                          )}

                          {values.isAllUsersBookAllowed === false && (
                            <>
                              <UserGroupListContainer>
                                <ListPreview
                                  placeholder={t('userForm.travelerUsersAllowed.placeholder')}
                                  icon="user"
                                  onAdd={() => setGroupsAndUsersSelectionVisible(true)}
                                  onClick={() => setGroupsAndUsersSelectionVisible(true)}
                                  previewItems={[...values.usersBookAllowed, ...values.groupsBookAllowed].map(
                                    (item) => ({
                                      title: `${item.name} ${item.lastName || ''}`,
                                      subtitle: item.entity === 'user' ? item.email : '',
                                      iconType: item.entity === 'user' ? 'initials' : 'group',
                                    }),
                                  )}
                                  noBorder
                                />
                              </UserGroupListContainer>
                              <ApplicationOverlayContent
                                sidebar
                                show={isGroupAndUsersSelectionVisible}
                                onClose={() => setGroupsAndUsersSelectionVisible(false)}
                              >
                                <GroupsAndUsersSelection
                                  userList={userListSelect}
                                  selectedUsers={values.usersBookAllowed}
                                  groupList={groupListSelect}
                                  selectedGroups={values.groupsBookAllowed}
                                  isLoading={isGroupFetching || isUserFetching}
                                  onConfirmSelection={(items: GroupedListModel[]) => {
                                    setValues({
                                      ...values,
                                      usersBookAllowed: items.filter((item) => item.entity === 'user'),
                                      groupsBookAllowed: items.filter((item) => item.entity === 'group'),
                                    });
                                  }}
                                  onCloseSelectionScreen={() => setGroupsAndUsersSelectionVisible(false)}
                                />
                              </ApplicationOverlayContent>
                            </>
                          )}
                        </>
                      )}
                    </ToggleCard>

                    <ToggleCard
                      title={t('userForm.whatsAppTitle')}
                      message={t('userForm.whatsAppDescription')}
                      value={values.sendWhatsapp}
                      onToggle={(_, value) => setFieldValue('sendWhatsapp', value ? true : false)}
                      errorMessage={!isValidPhoneNumber(values.phoneNumber) ? t('error.whatsappError') : ''}
                      isDisabledToggle={!isValidPhoneNumber(values.phoneNumber)}
                    />
                  </Section>

                  <SubmitButton dirty={dirty} isEdit={!!userData?.id} loading={isLoading}>
                    {t(`${stringGroup}.confirmButton`)}
                  </SubmitButton>
                </FormContainer>
              </>
            )}
          </Formik>
          <BottomSheet
            show={confirm}
            title={t(`${stringGroup}.modalConfirmTitle`)}
            content={
              data?.type === 'admin'
                ? t(`${stringGroup}.modalConfirmAdmText`)
                : t(`${stringGroup}.modalConfirmTravelerText`)
            }
            onClose={() => setConfirm(false)}
            onConfirm={save}
          />
          <ResponseScreen
            text={data?.id ? t('usersSuccess.edit') : t('usersSuccess.text')}
            subText={
              <>
                {htmlT(t, data?.id ? 'usersSuccess.editSubText' : 'usersSuccess.subText')}
                <TextTypeUser>{data?.email?.toLowerCase()}</TextTypeUser>
              </>
            }
            background={BackgroundTypes.Mountain}
            buttonOnClick={() => history.push('/usersList')}
            show={showSuccess}
          />
        </>
      )}
    </Wrapper>
  );
};

export default UsersForm;
